import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Bell, Home, Users, BookOpen, DollarSign, Settings, LogOut, UserCheck, User, Languages, FileCheck, ClipboardCheck, TrendingUp, Megaphone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import AdminOverview from './admin/AdminOverview';
import PendingApprovals from './admin/PendingApprovals';
import UserManagement from './admin/UserManagement';
import BookManagement from './admin/BookManagement';
import BookCompare from './admin/BookCompare';
import AdminProofreading from './admin/AdminProofreading';
import AddBook from './admin/AddBook';
import PreviewPublication from './admin/PreviewPublication';
import PaymentDashboard from './admin/PaymentDashboard';
import EarningsManagement from './admin/EarningsManagement';
import AdminSettings from './admin/AdminSettings';
import CloudinaryMigration from './admin/CloudinaryMigration';
import TranslationBooksManagement from './admin/TranslationBooksManagement';
import FaultListReview from './admin/FaultListReview';
import AmendmentsVerification from './admin/AmendmentsVerification';
import Adverteer from './admin/Adverteer';
import EmailWriters from './admin/EmailWriters';
import NotificationPanel from '../components/NotificationPanel';
import UserProfile from './shared/UserProfile';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [stats, setStats] = useState({});

  // Check if user is main admin (not handler)
  const isMainAdmin = user?.is_main_admin === true;

  useEffect(() => {
    fetchNotifications();
    fetchStats();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/admin`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Build nav items based on user role
  const navItems = [
    { path: '/admin', icon: Home, label: 'Oorsig', badge: null },
    { path: '/admin/approvals', icon: UserCheck, label: 'Nuwe Aansoeke', badge: stats.pending_proofreaders },
    { path: '/admin/users', icon: Users, label: 'Gebruikers', badge: null },
    { path: '/admin/books', icon: BookOpen, label: 'Boeke Bestuur', badge: stats.pending_approvals },
    { path: '/admin/translation-books', icon: Languages, label: 'Vertaling Boeke', badge: null },
    { path: '/admin/fault-review', icon: FileCheck, label: 'Foute Goedkeuring', badge: stats.pending_fault_lists },
    { path: '/admin/amendments', icon: ClipboardCheck, label: 'Regstellings Verifikasie', badge: stats.pending_amendments },
    { path: '/admin/adverteer', icon: Megaphone, label: 'Adverteer', badge: null },
    // Payment & settings options only visible to main admin
    ...(isMainAdmin ? [
      { path: '/admin/email-writers', icon: Mail, label: 'E-pos Skrywers', badge: null },
      { path: '/admin/payments', icon: DollarSign, label: 'Betalings', badge: stats.pending_payments },
      { path: '/admin/earnings', icon: TrendingUp, label: 'Verdienste Bestuur', badge: null },
      { path: '/admin/settings', icon: Settings, label: 'Instellings', badge: null },
    ] : []),
    { path: '/admin/profiel', icon: User, label: 'Profiel', badge: null },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      <header className="bg-white border-b-2 border-orange-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>DIE BIBLIOTEEK</h1>
              <p className="text-sm text-gray-600">Admin Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Button variant="ghost" size="icon" onClick={() => setShowNotifications(!showNotifications)}>
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500">{unreadCount}</Badge>
                  )}
                </Button>
                {showNotifications && <NotificationPanel notifications={notifications} onClose={() => setShowNotifications(false)} onRefresh={fetchNotifications} />}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.voornaam} {user?.van}</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <Button variant="outline" size="sm" onClick={logout}><LogOut className="h-4 w-4 mr-2" />Meld Af</Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <aside className="w-full md:w-64 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button variant={isActive ? 'default' : 'ghost'} className={`w-full justify-start gap-3 ${isActive ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' : 'hover:bg-orange-100'}`}>
                    <item.icon className="h-5 w-5" />
                    {item.label}
                    {item.badge > 0 && <Badge className="ml-auto bg-red-500">{item.badge}</Badge>}
                  </Button>
                </Link>
              );
            })}
          </aside>

          <main className="flex-1">
            <Routes>
              <Route index element={<AdminOverview stats={stats} />} />
              <Route path="approvals" element={<PendingApprovals onUpdate={fetchStats} />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="books" element={<BookManagement onUpdate={fetchStats} />} />
              <Route path="books/proeflees/:bookId" element={<AdminProofreading />} />
              <Route path="books/compare/:bookId" element={<BookCompare />} />
              <Route path="books/add" element={<AddBook />} />
              <Route path="books/migration" element={<CloudinaryMigration />} />
              <Route path="publication-preview/:bookId" element={<PreviewPublication />} />
              <Route path="translation-books" element={<TranslationBooksManagement />} />
              <Route path="fault-review" element={<FaultListReview />} />
              <Route path="amendments" element={<AmendmentsVerification />} />
              <Route path="adverteer" element={<Adverteer />} />
              {/* Payment & settings routes - only accessible to main admin */}
              {isMainAdmin && (
                <>
                  <Route path="payments" element={<PaymentDashboard />} />
                  <Route path="earnings" element={<EarningsManagement />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="email-writers" element={<EmailWriters />} />
                </>
              )}
              <Route path="profiel" element={<UserProfile />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;